import React from 'react';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { Typography } from '@vibTheme/components/_base/Typography';
import { isVariationPositive, resolveVariation } from '@utils/resolveVariation';

type Props = {
  current: number;
  previous: number;
  /** For metrics where going up is bad: refunds, and the refund rate. */
  invertLogic?: boolean;
};

/**
 * Period-over-period badge.
 *
 * Extracted out of SalesCharts because several cards now need it, and the arithmetic moved to
 * resolveVariation. The version that lived inline divided by the previous period without guarding it:
 * an empty previous period produced Infinity, two empty periods produced NaN, and both were papered
 * over - the first as a flat "100%", the second as "0%". Neither was a measurement.
 *
 * Growth from zero is shown as "novo" rather than a percentage, because there is no percentage to show.
 * Nothing to compare renders nothing at all, instead of implying stability.
 */
export const ProfitIndicator = ({
  current,
  previous,
  invertLogic = false,
}: Props) => {
  const variation = resolveVariation(current, previous);

  if (variation.kind === 'none') return null;

  const isPositive = isVariationPositive(variation, invertLogic);
  const tagClassname =
    isPositive === null
      ? 'bg-[#f3f4f6] text-[#374151]'
      : isPositive
      ? 'bg-[#d1fae5] text-[#065f46]'
      : 'bg-[#fee2e2] text-[#991b1b]';

  const isUp = variation.kind === 'new' || variation.direction === 'up';

  return (
    <div
      className={`flex justify-center items-center rounded-[12px] h-[24px] min-w-[64px] px-[4px] ${tagClassname}`}
      title={
        variation.kind === 'new'
          ? 'Não havia registro no período anterior, então não há percentual de comparação.'
          : undefined
      }
    >
      {variation.kind === 'change' &&
      variation.direction === 'flat' ? null : isUp ? (
        <ArrowDropUpRoundedIcon fontSize="small" />
      ) : (
        <ArrowDropDownRoundedIcon fontSize="small" />
      )}
      <Typography className="text-xs pr-[0.25rem]">
        {variation.kind === 'new' ? 'novo' : `${Math.abs(variation.percent)}%`}
      </Typography>
    </div>
  );
};
